import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { timeout } from 'rxjs/operators';
import { environment } from 'src/environments/environment.prod';

export interface UploadResponse {
    success: boolean;
    message: string;
    uploadedFiles?: string[];
    errors?: string[];
}

export interface PromoListResponse {
    message: string;
    motorInsurancePromotionList: PromoItem[];
}

export interface PromoItem {
    agent: string;
    approvedBy: string;
    vrn: string;
    policyNo: string;
    issueDate: string;
}

@Injectable({
    providedIn: 'root'
})
export class IcecashReportsService {

    private baseUrl = environment.apiUrl;

    constructor(private http: HttpClient) { }

    /**
     * Upload Excel file for Ice Cash report processing
     * @param formData - FormData containing the file as binary string
     * @returns Observable with upload progress and response
     */
    uploadFiles(formData: FormData): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/api/icecash-report-upload/upload`, formData).pipe(
            timeout(300000)
        );
    }

    uploadPromoFiles(formData: FormData): Observable<any> {
        return this.http.post<any>(`${this.baseUrl}/api/promotion-report-upload/upload`, formData).pipe(
            timeout(300000)
        );
    }

    fetchPromoItems(dateFrom: string, dateTo: string, status: string, region: string, promotionId: number): Observable<any> {
        const reqBody = {
            dateFrom: dateFrom,
            dateTo: dateTo,
            status: status,
            region: region,
            promotionId: promotionId
        }
        return this.http.get<any>(`${environment.apiUrl}` + '/api/promotion-report-upload/run-promotion', { params: reqBody })
    }

    /**
     * Get upload history for Ice Cash reports
     * @param page - Page number for pagination
     * @param limit - Number of items per page
     * @returns Observable with upload history
     */
    getUploadHistory(page: number = 1, limit: number = 10): Observable<any> {
        return this.http.get(`${this.baseUrl}/api/ice-cash-report-upload/history`, {
            params: { page: page.toString(), limit: limit.toString() }
        });
    }


    /**
     * Get processing status of uploaded files
     * @param uploadId - Upload ID to check status
     * @returns Observable with processing status
     */
    getProcessingStatus(uploadId: string): Observable<any> {
        return this.http.get(`${this.baseUrl}/api/ice-cash-report-upload/status/${uploadId}`);
    }

    /**
     * Download processed report
     * @param reportId - Report ID to download
     * @returns Observable with file blob
     */
    downloadReport(reportId: string): Observable<Blob> {
        return this.http.get(`${this.baseUrl}/api/ice-cash-report-upload/download/${reportId}`, {
            responseType: 'blob'
        });
    }

    /**
     * Delete uploaded file
     * @param fileId - File ID to delete
     * @returns Observable with deletion response
     */
    deleteFile(fileId: string): Observable<any> {
        return this.http.delete(`${this.baseUrl}/api/ice-cash-report-upload/files/${fileId}`);
    }

    /**
     * Get file validation rules
     * @returns Observable with validation rules
     */
    getValidationRules(): Observable<any> {
        return this.http.get(`${this.baseUrl}/api/ice-cash-report-upload/validation-rules`);
    }
} 